import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { Skel } from "@/components/Skeletons";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Check, Copy, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export default function Ruta() {
    const [route, setRoute] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [tpl, setTpl] = useState(null);

    const load = useCallback(async () => {
        try {
            const [r, t] = await Promise.all([api.get("/route"), api.get("/templates")]);
            setRoute(r.data);
            setTemplates(t.data.templates);
        } catch (e) {
            toast.error(errMsg(e));
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggle = async (stepId) => {
        try {
            const { data } = await api.post(`/route/steps/${stepId}/toggle`);
            setRoute((prev) => {
                const levels = prev.levels.map((lvl) => ({
                    ...lvl,
                    steps: lvl.steps.map((s) => (s.id === stepId ? { ...s, completed: data.completed } : s)),
                }));
                levels.forEach((lvl) => { lvl.completed = lvl.steps.filter((s) => s.completed).length; });
                const total = levels.reduce((a, l) => a + l.total, 0);
                const done = levels.reduce((a, l) => a + l.completed, 0);
                return { ...prev, levels, progreso: total ? Math.round((done / total) * 100) : 0, completados: done, total };
            });
        } catch (e) { toast.error(errMsg(e)); }
    };

    const openTemplate = (key) => {
        const t = templates.find((x) => x.key === key);
        if (t) setTpl(t);
    };

    const copyTpl = () => {
        navigator.clipboard?.writeText(tpl.body.replace("{nombre}", "[nombre]"));
        toast.success("Plantilla copiada");
    };

    if (!route) {
        return (
            <div className="space-y-6">
                <Skel className="h-9 w-48" />
                <Skel className="h-24 w-full rounded-2xl" />
                <Skel className="h-96 w-full rounded-2xl" />
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="ruta-page">
            <div>
                <h1 className="text-3xl font-bold tracking-tight text-nm-ink">Mi ruta</h1>
                <p className="mt-1 text-nm-muted">Paso a paso, sin depender de nadie. Marca lo que vayas completando.</p>
            </div>

            <div className="rounded-2xl border border-nm-line bg-nm-surface p-6">
                <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-nm-ink">Progreso general</p>
                    <p className="text-sm font-semibold text-nm-accent tabular-nums">
                        {route.completados}/{route.total} · {route.progreso}%
                    </p>
                </div>
                <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-nm-line">
                    <div className="h-full rounded-full bg-nm-accent transition-all duration-500" style={{ width: `${route.progreso}%` }} data-testid="route-progress-bar" />
                </div>
            </div>

            <div className="space-y-6">
                {route.levels.map((lvl) => (
                    <section key={lvl.level} className="overflow-hidden rounded-2xl border border-nm-line bg-nm-surface" data-testid={`route-level-${lvl.level}`}>
                        <div className="flex items-center justify-between border-b border-nm-line bg-nm-accent-soft/50 px-6 py-4">
                            <div>
                                <p className="text-xs font-medium uppercase tracking-wider text-nm-accent">Nivel {lvl.level}</p>
                                <h2 className="mt-0.5 text-lg font-semibold text-nm-ink">{lvl.title}</h2>
                                <p className="mt-0.5 text-sm text-nm-muted">{lvl.subtitle}</p>
                            </div>
                            <span className="shrink-0 text-sm font-semibold text-nm-muted tabular-nums">{lvl.completed}/{lvl.total}</span>
                        </div>
                        <div className="divide-y divide-nm-line">
                            {lvl.steps.map((s) => (
                                <div key={s.id} className="flex items-start gap-4 px-6 py-4" data-testid={`route-step-${s.id}`}>
                                    <button onClick={() => toggle(s.id)} data-testid={`route-toggle-${s.id}`}
                                        className={cn("mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md border transition-colors duration-150",
                                            s.completed ? "border-nm-accent bg-nm-accent text-white" : "border-nm-line hover:border-nm-accent")}
                                        aria-label={s.completed ? "Marcar como pendiente" : "Marcar como hecho"}>
                                        {s.completed && <Check className="h-4 w-4" />}
                                    </button>
                                    <div className="min-w-0 flex-1">
                                        <p className={cn("font-semibold", s.completed ? "text-nm-muted line-through" : "text-nm-ink")}>{s.title}</p>
                                        <p className="mt-1 text-sm leading-relaxed text-nm-muted">{s.description}</p>
                                        {s.template && (
                                            <button onClick={() => openTemplate(s.template)}
                                                className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-nm-accent"
                                                data-testid={`route-template-${s.id}`}>
                                                <FileText className="h-3.5 w-3.5" /> Ver plantilla
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                ))}
            </div>

            {/* Todas las plantillas */}
            <section>
                <h2 className="mb-4 text-lg font-semibold text-nm-ink">Plantillas de mensajes</h2>
                <div className="grid gap-3 sm:grid-cols-2">
                    {templates.map((t) => (
                        <button key={t.key} onClick={() => setTpl(t)} data-testid={`template-card-${t.key}`}
                            className="rounded-2xl border border-nm-line bg-nm-surface p-4 text-left transition-colors duration-150 hover:border-nm-accent/40">
                            <p className="font-semibold text-nm-ink">{t.name}</p>
                            <p className="mt-1 text-sm text-nm-muted">{t.description}</p>
                        </button>
                    ))}
                </div>
            </section>

            <Dialog open={!!tpl} onOpenChange={(o) => !o && setTpl(null)}>
                <DialogContent className="max-w-lg rounded-2xl" data-testid="template-dialog">
                    {tpl && (
                        <>
                            <DialogHeader>
                                <DialogTitle>{tpl.name}</DialogTitle>
                                <DialogDescription>{tpl.description}</DialogDescription>
                            </DialogHeader>
                            <div className="rounded-xl border border-nm-line bg-nm-bg p-4 text-sm leading-relaxed text-nm-ink">
                                {tpl.body.split("{nombre}").reduce((acc, part, i) => {
                                    if (i === 0) return [part];
                                    return [...acc, <span key={i} className="font-semibold text-nm-accent">[nombre]</span>, part];
                                }, [])}
                            </div>
                            <div className="flex justify-end">
                                <Button onClick={copyTpl} data-testid="template-copy"><Copy className="mr-1 h-4 w-4" /> Copiar</Button>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
